import type { AuthenticationSession } from "./AuthenticationSession";
import {
  consumeReturnTo,
  saveReturnTo,
  sanitizeReturnTo,
} from "./returnTo";

type BootstrapArgs = Readonly<{
  session: AuthenticationSession;
  location: Pick<Location, "pathname" | "search" | "hash">;
  history: Pick<History, "replaceState">;
  renderAuthenticated: () => void;
  renderFailure: (retry: () => void) => void;
  providerAvailable?: () => Promise<void>;
}>;

export async function bootstrapApplication(args: BootstrapArgs): Promise<void> {
  const {
    session,
    location,
    history,
    renderAuthenticated,
    renderFailure,
    providerAvailable,
  } = args;

  /*
   * Ghi nhớ trang đang đứng để quay về sau khi đăng nhập.
   *
   * `sanitizeReturnTo` cắt chất liệu OIDC (`#state=…&code=…` — keycloak-js trả kết quả
   * trong fragment) nên URL callback vẫn cho ra đúng đường dẫn sạch. Nhờ vậy đường
   * "token hết hạn khi đang ở trang B" — callback quay về chính `/trang-b#state=…` —
   * vẫn giữ được đích đến, thay vì bị đá về trang chủ.
   *
   * Bỏ qua khi kết quả là `/`: lúc đó không học được gì mới, mà `saveReturnTo("/")` sẽ
   * XOÁ giá trị đã lưu — đúng thứ cần giữ khi callback về ở gốc.
   */
  const currentRoute = `${location.pathname}${location.search}${location.hash}`;
  if (sanitizeReturnTo(currentRoute) !== "/") {
    saveReturnTo(currentRoute);
  }

  if (providerAvailable) {
    try {
      await providerAvailable();
    } catch {
      renderFailure(() => window.location.reload());
      return;
    }
  }

  try {
    await session.initialize();
  } catch {
    renderFailure(() => window.location.reload());
    return;
  }

  const safeReturnTo = consumeReturnTo();
  const targetRoute = safeReturnTo === "/" || safeReturnTo === "/login" ? "/martyrs" : safeReturnTo;
  history.replaceState(null, "", sanitizeReturnTo(targetRoute));
  renderAuthenticated();
}